// src/components/admin/AdminReturnRequests.jsx

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiRefresh, HiInformationCircle } from "react-icons/hi";
import { Package, FileText, ChevronRight } from "lucide-react";
import {
  fetchReturnOrders,
  updateDeliveryStatus,
  updateOrderStatus,
} from "../../features/orderSlice";
import { motion } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import ConfirmModal from "./ConfirmModal";
import DatePicker from "react-datepicker";
import axiosInstance from "../../utils/axiosInstance";

const NEXT_RETURN = {
  RETURN_REQUESTED: ["PICKUP_SCHEDULED"],
  PICKUP_SCHEDULED: ["RETURN_IN_TRANSIT"],
  RETURN_IN_TRANSIT: ["RETURNED"],
  RETURNED: [],
};

// Return status badge configuration
const RETURN_STATUS_CONFIG = {
  RETURN_REQUESTED: {
    label: "Requested",
    color: "bg-gray-100/80 text-gray-700 border-gray-200/80 dark:bg-gray-800/80 dark:text-gray-200 dark:border-gray-700/80",
    dotColor: "bg-gray-500 dark:bg-gray-400",
  },
  PICKUP_SCHEDULED: {
    label: "Pickup Scheduled",
    color: "bg-blue-50/80 text-blue-700 border-blue-200/80 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/80",
    dotColor: "bg-blue-500 dark:bg-blue-400",
  },
  RETURN_IN_TRANSIT: {
    label: "In Transit",
    color: "bg-indigo-50/80 text-indigo-700 border-indigo-200/80 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800/80",
    dotColor: "bg-indigo-500 dark:bg-indigo-400",
  },
  RETURNED: {
    label: "Returned",
    color: "bg-green-50/80 text-green-700 border-green-200/80 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800/80",
    dotColor: "bg-green-500 dark:bg-green-400",
  },
  COMPLETED: {
    label: "Completed",
    color: "bg-gray-100/80 text-gray-600 border-gray-200/80 dark:bg-gray-800/60 dark:text-gray-400 dark:border-gray-700/80",
    dotColor: "bg-gray-400 dark:bg-gray-500",
  },
};

// Workflow steps for visualization
const RETURN_WORKFLOW_STEPS = ["RETURN_REQUESTED", "PICKUP_SCHEDULED", "RETURN_IN_TRANSIT", "RETURNED"];

export default function AdminReturnRequests() {
  const dispatch = useDispatch();
  const { returnOrders = [], status } = useSelector((s) => s.orders);
  const [confirmData, setConfirmData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"

  useEffect(() => {
    dispatch(fetchReturnOrders());
  }, [dispatch]);

  const advanceReturnStatus = (order) => {
    const [next] = NEXT_RETURN[order.deliveryStatus] || [];
    if (!next) return;
    setConfirmData({
      orderId: order.orderId,
      nextStatus: next,
      actionType: "advance",
    });
  };

  const completeOrder = (orderId) => {
    setConfirmData({
      orderId,
      nextStatus: "COMPLETED",
      actionType: "complete",
    });
  };

  const onConfirm = () => {
    const { orderId, nextStatus, actionType } = confirmData;

    const dispatchAction =
      actionType === "advance"
        ? updateDeliveryStatus({ orderId, deliveryStatus: nextStatus })
        : updateOrderStatus({ orderId, status: nextStatus });

    dispatch(dispatchAction)
      .unwrap()
      .then(() => {
        toast.success(
          actionType === "advance"
            ? "Return status updated"
            : "Order marked completed"
        );
        dispatch(fetchReturnOrders());
      })
      .catch((e) =>
        toast.error(
          `${actionType === "advance" ? "Update" : "Completion"} failed: ${
            e.message
          }`
        )
      );

    setConfirmData(null);
  };

  const onCancel = () => setConfirmData(null);

  // Helper to format a "date array" such as [2025, 5, 28, 10, 3, 57, ...]
  const formatDateArray = (arr) => {
    if (!Array.isArray(arr) || arr.length < 3) return { dateStr: "—", timeStr: "" };
    const [year, month, day, hour = 0, minute = 0] = arr;
    const d = new Date(year, month - 1, day, hour, minute);
    
    const dateStr = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    
    const timeStr = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).toUpperCase();
    
    return { dateStr, timeStr };
  };

  function formatLocalDateAsYYYYMMDD(d) {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const handleGenerateReturnPdf = async () => {
    if (!selectedDate) return toast.error("Please select a date");

    setDownloading(true);
    try {
      const formatted = formatLocalDateAsYYYYMMDD(selectedDate);
      const response = await axiosInstance.get(`/pdf/returns?date=${formatted}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `returns_${formatted}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Return PDF generated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate return PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Sort return orders by requested date
  const sortedReturnOrders = [...returnOrders].sort((a, b) => {
    const dateA = a.statusTimestamps?.RETURN_REQUESTED || a.creationDate || [];
    const dateB = b.statusTimestamps?.RETURN_REQUESTED || b.creationDate || [];
    
    if (!Array.isArray(dateA) || dateA.length < 3) return 1;
    if (!Array.isArray(dateB) || dateB.length < 3) return -1;
    
    const timeA = new Date(dateA[0], dateA[1] - 1, dateA[2], dateA[3] || 0, dateA[4] || 0).getTime();
    const timeB = new Date(dateB[0], dateB[1] - 1, dateB[2], dateB[3] || 0, dateB[4] || 0).getTime();
    
    return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
  });

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <Toaster position="top-center" />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header - Clean & Modern */}
        <header className="space-y-1">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Return Management
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Manage return workflow and status
              </p>
            </div>
            
            <button
              onClick={() => dispatch(fetchReturnOrders())}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors duration-150"
              aria-label="Refresh returns"
            >
              <HiRefresh className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </header>

        {/* Subtle Guidance Banner */}
        <div className="flex items-start gap-3 p-3.5 bg-gray-100/60 dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/80 rounded-lg">
          <HiInformationCircle className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            Advance returns one step at a time (Requested → Pickup Scheduled → In Transit → Returned). Once returned, you may mark it completed.
          </p>
        </div>

        {/* PDF Generation Section */}
        <div className="flex items-center gap-3 p-3.5 bg-white dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/80 rounded-xl shadow-sm dark:shadow-black/10">
          <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select return date"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
          />
          <button
            disabled={!selectedDate || downloading}
            onClick={handleGenerateReturnPdf}
            className="px-3.5 py-2 text-sm font-medium rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-sm"
          >
            {downloading ? "Generating..." : "Generate PDF"}
          </button>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-3 p-3.5 bg-white dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/80 rounded-xl shadow-sm dark:shadow-black/10">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by Date:</span>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>

        {/* Content */}
        {status === "loading" ? (
          <div className="text-center py-20 text-gray-500 dark:text-gray-400">
            Loading return requests…
          </div>
        ) : sortedReturnOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80">
            <Package className="w-12 h-12 text-gray-300 dark:text-gray-700 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No return requests</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">All returns have been processed</p>
          </div>
        ) : (
          <div className="overflow-hidden bg-white dark:bg-gray-900/60 rounded-xl shadow-xl dark:shadow-black/20 border border-gray-200/60 dark:border-gray-800/80">
            <div className="overflow-x-auto custom-scrollbar">
              <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225 / 0.6);
                  border-radius: 3px;
                  transition: background-color 0.15s;
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105 / 0.8);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(148 163 184);
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800/80">
                <thead className="bg-gray-50/60 dark:bg-gray-900/80 border-b border-gray-200/60 dark:border-gray-800/80">
                  <tr>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Book
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Requested On
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Return Status
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>

                <motion.tbody
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="divide-y divide-gray-100 dark:divide-gray-800/60"
                >
                  {sortedReturnOrders.map((o) => {
                    const requestedArr = o.statusTimestamps?.RETURN_REQUESTED || [];
                    const fallbackArr = o.creationDate || [];
                    const { dateStr, timeStr } =
                      requestedArr.length > 0
                        ? formatDateArray(requestedArr)
                        : fallbackArr.length > 0
                        ? formatDateArray(fallbackArr)
                        : { dateStr: "—", timeStr: "" };

                    const next = NEXT_RETURN[o.deliveryStatus]?.[0];
                    const currentStepIndex = RETURN_WORKFLOW_STEPS.indexOf(o.deliveryStatus);
                    const isReturned = o.deliveryStatus === "RETURNED";

                    return (
                      <tr
                        key={o.orderId}
                        className="hover:bg-gray-50/80 dark:hover:bg-white/[0.05] transition-all duration-150 hover:shadow-sm dark:hover:shadow-black/10"
                      >
                        <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          #{o.orderId}
                        </td>

                        <td className="px-3 py-3">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 max-w-xs truncate">
                            {o.book.bookName}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {o.user.firstName} {o.user.lastName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {o.user.emailId ?? "–"}
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="space-y-0.5">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
                              {dateStr}
                            </div>
                            {timeStr && (
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {timeStr}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-150 hover:shadow-sm ${RETURN_STATUS_CONFIG[o.deliveryStatus]?.color || RETURN_STATUS_CONFIG.RETURN_REQUESTED.color}`}>
                              {RETURN_STATUS_CONFIG[o.deliveryStatus]?.label || o.deliveryStatus.replace(/_/g, " ")}
                            </span>
                            
                            {currentStepIndex >= 0 && (
                              <div className="flex items-center">
                                {RETURN_WORKFLOW_STEPS.map((step, idx) => {
                                  const isActive = idx <= currentStepIndex;
                                  const isCurrent = idx === currentStepIndex;
                                  return (
                                    <div key={step} className="flex items-center">
                                      <div
                                        className={`rounded-full transition-all duration-200 ${
                                          isCurrent 
                                            ? "w-1.5 h-1.5" 
                                            : "w-1 h-1"
                                        } ${
                                          isActive
                                            ? RETURN_STATUS_CONFIG[o.deliveryStatus]?.dotColor || "bg-gray-400"
                                            : "bg-gray-300 dark:bg-gray-700 opacity-40"
                                        }`}
                                      />
                                      {idx < RETURN_WORKFLOW_STEPS.length - 1 && (
                                        <div
                                          className={`w-2.5 h-[1.5px] transition-all duration-200 ${
                                            idx < currentStepIndex
                                              ? RETURN_STATUS_CONFIG[o.deliveryStatus]?.dotColor || "bg-gray-400"
                                              : "bg-gray-300 dark:bg-gray-700 opacity-30"
                                          }`}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          {next ? (
                            <button
                              onClick={() => advanceReturnStatus(o)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-gray-100 transition-all duration-150"
                            >
                              {RETURN_STATUS_CONFIG[next]?.label || next.replace(/_/g, " ")}
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          ) : isReturned ? (
                            <button
                              onClick={() => completeOrder(o.orderId)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-150"
                            >
                              Mark Completed
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-600">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </motion.tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {confirmData && (
        <ConfirmModal
          isOpen={true}
          title={
            confirmData.actionType === "advance"
              ? `Advance Return #${confirmData.orderId}?`
              : `Complete Return #${confirmData.orderId}?`
          }
          message={`Are you sure you want to ${
            confirmData.actionType === "advance" ? "advance" : "mark completed"
          } return #${confirmData.orderId} to "${
            confirmData.nextStatus.replace(/_/g, " ")
          }"?`}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}
